import { createClient } from '@supabase/supabase-js'
import type { Session } from '@supabase/supabase-js'

// Replace with your Supabase project details
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase environment variables are not set.')
}

// 1. Create a single Supabase client instance
const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types
export interface JobPosition {
  id: string
  title: string
  department: string
  location: string
  type: 'full-time' | 'part-time' | 'contract'
  experience: string
  salary: string
  description: string
  job_description: string[]
  requirements: string[]
  benefits: string[]
  posted_date: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ActivityEvent {
  id: string
  title: string
  description: string
  date: string
  location: string
  attendees: string
  duration: string
  category: string
  icon_name: string
  color_scheme: string
  image_url: string
  video_url: string | null
  youtube_url: string | null
  media_type: 'image' | 'video' | 'youtube'
  carousel_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface JobNotification {
  id: string
  email: string
  is_active: boolean
  created_at: string
  notified_at: string | null
}

// 2. Define and export a 'db' object with helper functions that use the 'supabase' client
export const db = {
  // Job positions
  jobs: {
    async getAll() {
      const { data, error } = await supabase
        .from('job_positions')
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data as JobPosition[]
    },

    async getActive() {
      const { data, error } = await supabase
        .from('job_positions')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data as JobPosition[]
    },

    async getById(id: string) {
      const { data, error } = await supabase
        .from('job_positions')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) throw error
      return data as JobPosition
    },

    async create(job: Omit<JobPosition, 'id' | 'created_at' | 'updated_at'>) {
      const { data, error } = await supabase
        .from('job_positions')
        .insert([job])
        .select()
        .single()
      
      if (error) throw error
      return data as JobPosition
    },

    async update(id: string, job: Partial<JobPosition>) {
      const { data, error } = await supabase
        .from('job_positions')
        .update({ ...job, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data as JobPosition
    },

    async delete(id: string) {
      const { error } = await supabase
        .from('job_positions')
        .delete()
        .eq('id', id)
      
      if (error) throw error
    }
  },

  // Activities
  activities: {
    async getAll() {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .order('date', { ascending: false })
      
      if (error) throw error
      return data as ActivityEvent[]
    },

    async getActive() {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('is_active', true)
        .order('carousel_order', { ascending: true })
      
      if (error) throw error
      return data as ActivityEvent[]
    },

    async getById(id: string) {
      const { data, error } = await supabase
        .from('activities')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error) throw error
      return data as ActivityEvent
    },

    async create(activity: Omit<ActivityEvent, 'id' | 'created_at' | 'updated_at'>) {
      const { data, error } = await supabase
        .from('activities')
        .insert([activity])
        .select()
        .single()
      
      if (error) throw error
      return data as ActivityEvent
    },

    async update(id: string, activity: Partial<ActivityEvent>) {
      const { data, error } = await supabase
        .from('activities')
        .update({ ...activity, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data as ActivityEvent
    },

    async delete(id: string) {
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', id)
      
      if (error) throw error
    }
  },

  // Job notifications
  jobNotifications: {
    async subscribe(email: string) {
      // Check if already subscribed
      const { data: existing } = await supabase
        .from('job_notifications')
        .select('*')
        .eq('email', email)
        .single()
      
      if (existing) {
        return { success: false, message: 'Already subscribed' }
      }

      const { data, error } = await supabase
        .from('job_notifications')
        .insert([{ 
          email, 
          is_active: true,
          created_at: new Date().toISOString()
        }])
        .select()
        .single()
      
      if (error) throw error
      return { success: true, data: data as JobNotification }
    },

    async unsubscribe(email: string) {
      const { error } = await supabase
        .from('job_notifications')
        .update({ is_active: false })
        .eq('email', email)
      
      if (error) throw error
      return { success: true }
    },

    async getActiveSubscribers() {
      try {
        const { data, error } = await supabase
          .from('job_notifications')
          .select('*')
          .eq('is_active', true)
        
        if (error) {
          console.warn('Error fetching subscribers:', error);
          return [];
        }
        
        return data as JobNotification[]
      } catch (error) {
        console.warn('Table job_notifications might not exist:', error);
        return [];
      }
    },

    async markAsNotified(id: string) {
      const { error } = await supabase
        .from('job_notifications')
        .update({ notified_at: new Date().toISOString() })
        .eq('id', id)
      
      if (error) throw error
    }
  }
}

// 3. Define and export an 'auth' object for authentication functions
export const auth = {
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    if (error) throw error
    return data
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  async getSession() {
    const { data: { session } } = await supabase.auth.getSession()
    return session
  },

  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// 5. Storage functions for file uploads
export const storage = {
  async uploadFile(file: File, folder: string = 'activities') {
    const fileName = `${Date.now()}-${file.name}`
    const filePath = `${folder}/${fileName}`
    
    console.log('=== STORAGE UPLOAD DEBUG ===')
    console.log('File details:', { fileName, fileSize: file.size, fileType: file.type, folder, filePath })
    
    try {
      // First check if bucket exists, if not create it
      console.log('Checking if bucket exists...')
      const bucketExists = await this.checkBucket()
      console.log('Bucket exists check result:', bucketExists)
      
      if (!bucketExists) {
        console.log('Bucket does not exist, attempting to create...')
        try {
          const { data: bucketData, error: createError } = await supabase.storage.createBucket('media', {
            public: true,
            fileSizeLimit: 50 * 1024 * 1024, // 50MB
            allowedMimeTypes: ['image/*', 'video/*']
          })
          
          console.log('Bucket creation response:', { bucketData, createError })
          
          if (createError) {
            console.error('Bucket creation error:', createError)
            if (createError.message.includes('already exists')) {
              console.log('Bucket already exists, continuing...')
            } else if (createError.message.includes('permission')) {
              throw new Error('Nedostatočné oprávnenia na vytvorenie bucketu. Skontrolujte nastavenia Supabase Storage.')
            } else {
              throw createError
            }
          } else {
            console.log('Bucket created successfully:', bucketData)
          }
        } catch (createError: unknown) {
          if (createError instanceof Error) {
            if (createError.message.includes('already exists')) {
              console.log('Bucket already exists, continuing...')
            } else {
              throw new Error(`Failed to create storage bucket: ${createError.message}`)
            }
          } else {
            throw new Error('Unknown error during bucket creation')
          }
        }
      }
      
      console.log('Attempting to upload file to path:', filePath)
      console.log('Supabase client config:', { url: supabaseUrl, hasKey: !!supabaseAnonKey })
      
      const { data, error } = await supabase.storage
        .from('media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })
      
      console.log('Upload response:', { data, error })
      
      if (error) {
        console.error('Storage upload error details:', error)
        console.error('Error name:', error.name)
        console.error('Error message:', error.message)
        console.error('Error status:', error.status)
        
        if (error.message.includes('bucket not found')) {
          throw new Error('Storage bucket "media" nebol nájdený ani sa nepodarilo vytvoriť. Skontrolujte nastavenia Supabase.')
        }
        if (error.message.includes('permission')) {
          throw new Error('Nedostatočné oprávnenia na upload. Skontrolujte storage politiky v Supabase.')
        }
        if (error.message.includes('size')) {
          throw new Error('Súbor je príliš veľký. Maximálna veľkosť je 50MB.')
        }
        throw new Error(`Nahrávanie súboru zlyhalo: ${error.message}`)
      }
      
      if (!data) {
        throw new Error('Upload returned no data')
      }
      
      console.log('Upload successful, data:', data)
      console.log('Getting public URL for path:', data.path)
      
      const { data: publicUrlData, error: urlError } = supabase.storage
        .from('media')
        .getPublicUrl(data.path)
      
      if (urlError) {
        console.error('Error getting public URL:', urlError)
        throw new Error(`Chyba pri získavaní verejnej URL: ${urlError.message}`)
      }
      
      console.log('Public URL data:', publicUrlData)
      
      if (!publicUrlData?.publicUrl) {
        throw new Error('Nepodarilo sa získať verejnú URL')
      }
      
      console.log('File uploaded successfully, public URL:', publicUrlData.publicUrl)
      console.log('=== UPLOAD COMPLETE ===')
      
      return publicUrlData.publicUrl
    } catch (error: unknown) {
      console.error('=== UPLOAD FAILED ===')
      console.error('Storage upload error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
      throw new Error(`Chyba pri nahrávaní súboru: ${errorMessage}`)
    }
  },

  // Manual bucket creation function for admin panel
  async createMediaBucket() {
    try {
      console.log('Attempting to create media bucket manually...')
      const { data, error } = await supabase.storage.createBucket('media', {
        public: true,
        fileSizeLimit: 50 * 1024 * 1024, // 50MB
        allowedMimeTypes: ['image/*', 'video/*']
      })
      
      if (error) {
        console.error('Manual bucket creation failed:', error)
        if (error.message.includes('already exists')) {
          return { success: true, message: 'Bucket už existuje' }
        }
        throw error
      }
      
      console.log('Media bucket created successfully:', data)
      return { success: true, message: 'Bucket úspešne vytvorený', data }
    } catch (error: unknown) {
      console.error('Manual bucket creation error:', error)
      const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
      return { success: false, message: `Chyba: ${errorMessage}` }
    }
  },

  async deleteFile(fileUrl: string) {
    try {
      const urlParts = fileUrl.split('/')
      const fileName = urlParts[urlParts.length - 1]
      const folder = urlParts[urlParts.length - 2]
      
      const { error } = await supabase.storage
        .from('media')
        .remove([`${folder}/${fileName}`])
      
      if (error) {
        if (error.message.includes('bucket not found')) {
          console.warn('Storage bucket "media" nebol nájdený, súbor nebol odstránený.')
          return
        }
        throw error
      }
    } catch (error: unknown) {
      console.error('Storage delete error:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Unknown error during file deletion')
    }
  },

  // Pomocná funkcia na kontrolu existencie bucketu
  async checkBucket() {
    try {
      const { data, error } = await supabase.storage.getBucket('media')
      if (error) {
        console.error('Bucket check error:', error)
        return false
      }
      return true
    } catch (error: unknown) {
      console.error('Bucket check exception:', error)
      return false
    }
  },

  // Debug funkcia na zobrazenie všetkých bucketov
  async listBuckets() {
    try {
      const { data, error } = await supabase.storage.listBuckets()
      if (error) {
        console.error('List buckets error:', error)
        return []
      }
      console.log('Available buckets:', data)
      return data
    } catch (error: unknown) {
      console.error('List buckets exception:', error)
      return []
    }
  },

  // Comprehensive bucket diagnostics
  async diagnoseBucket() {
    console.log('=== BUCKET DIAGNOSTICS ===')
    
    try {
      // List all buckets
      const buckets = await this.listBuckets()
      console.log('Total buckets found:', buckets.length)
      
      // Check if media bucket exists
      const mediaBucket = buckets.find(b => b.name === 'media')
      console.log('Media bucket found:', !!mediaBucket)
      
      if (mediaBucket) {
        console.log('Media bucket details:', {
          id: mediaBucket.id,
          name: mediaBucket.name,
          public: mediaBucket.public,
          fileSizeLimit: mediaBucket.fileSizeLimit,
          allowedMimeTypes: mediaBucket.allowedMimeTypes,
          createdAt: mediaBucket.createdAt
        })
      }
      
      // Try to get bucket details directly
      try {
        const { data: bucketData, error: bucketError } = await supabase.storage.getBucket('media')
        console.log('Direct bucket check:', { bucketData, bucketError })
      } catch (getError) {
        console.error('Error getting bucket directly:', getError)
      }
      
      // Check if we can list objects in the bucket
      try {
        const { data: objects, error: listError } = await supabase.storage
          .from('media')
          .list('activities', { limit: 1 })
        console.log('Objects list test:', { objectsFound: objects?.length, listError })
      } catch (listError) {
        console.error('Error listing objects:', listError)
      }
      
      // Check environment variables
      console.log('Environment check:', {
        hasSupabaseUrl: !!supabaseUrl,
        hasSupabaseKey: !!supabaseAnonKey,
        urlLength: supabaseUrl?.length,
        keyLength: supabaseAnonKey?.length
      })
      
      console.log('=== DIAGNOSTICS COMPLETE ===')
      
      return {
        bucketExists: !!mediaBucket,
        bucketDetails: mediaBucket,
        totalBuckets: buckets.length,
        connectionValid: !!supabaseUrl && !!supabaseAnonKey
      }
      
    } catch (error: unknown) {
      console.error('Diagnostics error:', error)
      return {
        bucketExists: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }
}

// 4. Export the main 'supabase' client as the default export
export default supabase